/* Manet Inscription Customizer (100% English · Zero-Cart Direct Checkout Flow)
 * ----------------------------------------------------------------------------
 * 1. Zero Cart Policy (Mechanism C): Form submit creates Draft Order -> Direct to Checkout.
 * 2. Automatic Size & Price Detection: Content byte size -> Auto matches variant tier & price.
 * 3. Exclusive Inscription Modes: Mode A (Pure Text) vs Mode B (Single Media File).
 * 4. Advanced Settings: Title, Description, Scene attributes, BTC Address, Gallery Visibility (All Optional).
 * 5. 100% Pure English formatting across all inputs and placeholders.
 */
(function () {
  "use strict";

  var cfg = window.MANET_CONFIG || {};
  var TIER_KB = Number(cfg.tierKb) || 350;
  var HARD_LIMIT_BYTES = 350 * 1024; // 358,400 Bytes
  var DEFAULT_WORKER = "https://relay.manet.life";
  var WORKER_URL = (cfg.workerUrl || DEFAULT_WORKER).replace(/\/+$/, "");
  var SHARED_KEY = cfg.sharedKey || "ekekclskiengkxkehok4938530d308c";
  var ORDER_ID = (cfg.customer && cfg.customer.id) || (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) || "anon-" + Date.now();
  var pendingRawFile = null;

  // ── 100% English Scene Schemas (Text inputs with YYYY-MM-DD placeholders) ──
  var BUILTIN_SCHEMAS = {
    memorial: {
      code: "memorial",
      title: "In Loving Memory",
      metafields: [
        { key: "deceased_name", label: "Deceased Name", type: "text", placeholder: "e.g. Johnathan Smith" },
        { key: "birth_year", label: "Birth Year", type: "text", placeholder: "e.g. 1952" },
        { key: "death_year", label: "Death Year", type: "text", placeholder: "e.g. 2024" },
        { key: "relationship", label: "Relationship", type: "text", placeholder: "e.g. Beloved Father / Mother" },
        { key: "identity", label: "Key Life Milestone / Role", type: "text", placeholder: "e.g. Scholar, Mentor & Veteran" }
      ]
    },
    vow: {
      code: "vow",
      title: "Eternal Vow",
      metafields: [
        { key: "recipient_name", label: "Recipient Name", type: "text", placeholder: "e.g. My Beloved Emily" },
        { key: "relationship", label: "Relationship", type: "text", placeholder: "e.g. Wife / Partner" },
        { key: "vow_date", label: "Vow Date (YYYY-MM-DD)", type: "text", placeholder: "e.g. 2026-08-26" }
      ]
    },
    birth: {
      code: "birth",
      title: "Newborn Memorial",
      metafields: [
        { key: "child_name", label: "Child Name", type: "text", placeholder: "e.g. Alexander William" },
        { key: "birth_date", label: "Birth Date (YYYY-MM-DD)", type: "text", placeholder: "e.g. 2026-01-15" },
        { key: "birth_place", label: "Birth Place", type: "text", placeholder: "e.g. San Francisco, California" }
      ]
    },
    future: {
      code: "future",
      title: "Letter to the Future",
      metafields: [
        { key: "recipient", label: "Recipient", type: "text", placeholder: "e.g. To My Future Self / My Children" },
        { key: "open_date", label: "Unlock Date (YYYY-MM-DD)", type: "text", placeholder: "e.g. 2045-10-01" }
      ]
    },
    lineage: {
      code: "lineage",
      title: "Family Lineage",
      metafields: [
        { key: "heirloom", label: "Heirloom / Family Motto", type: "text", placeholder: "e.g. Honor, Integrity, Legacy" },
        { key: "heir", label: "Heir / Successor", type: "text", placeholder: "e.g. Eldest Grandchild" }
      ]
    },
    free: {
      code: "free",
      title: "Custom Inscription",
      metafields: []
    }
  };

  // State
  var activeMode = "text"; // "text" | "media"
  var selectedFile = null; // Single File object
  var selectedFileObjectUrl = null;
  var metaValues = {};
  var currentSceneCode = cfg.productScene || "memorial";
  var currentMatchedVariantId = null;
  var submitting = false;

  function utf8Bytes(str) {
    return new Blob([str || ""]).size;
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  }

  function detectFileType(file) {
    if (!file) return "text";
    var t = (file.type || "").toLowerCase();
    if (t.indexOf("image/") === 0) return "image";
    if (t.indexOf("audio/") === 0) return "audio";
    if (t.indexOf("video/") === 0) return "video";
    return "media";
  }

  // ── Automatic Size Recognition & Variant Price Matching (autoTier) ──────────
  function computeTier(totalBytes) {
    var maxB = TIER_KB * 1024;
    var variants = cfg.variants || [];

    var tiers = [
      { name: "Bronze Edition (0–30 KB)", max: 30 * 1024, tierIndex: 0 },
      { name: "Silver Edition (30–100 KB)", max: 100 * 1024, tierIndex: 1 },
      { name: "Gold Edition (100–200 KB)", max: 200 * 1024, tierIndex: 2 },
      { name: "Platinum Edition (200–300 KB)", max: 300 * 1024, tierIndex: 3 },
      { name: "Diamond Edition (300–350 KB)", max: 350 * 1024, tierIndex: 4 }
    ];

    var matchedTier = tiers[0];
    for (var i = 0; i < tiers.length; i++) {
      if (totalBytes <= tiers[i].max) {
        matchedTier = tiers[i];
        break;
      }
    }
    if (totalBytes > maxB) {
      matchedTier = { name: "Exceeds Maximum Capacity (" + TIER_KB + " KB)", over: true, tierIndex: tiers.length - 1 };
    }

    var matchedVariant = variants[matchedTier.tierIndex] || variants[variants.length - 1] || variants[0] || null;
    var priceStr = matchedVariant && matchedVariant.price ? matchedVariant.price : "";
    var varId = matchedVariant ? (matchedVariant.gid || matchedVariant.id) : null;

    return {
      bytes: totalBytes,
      maxBytes: maxB,
      pct: Math.min(100, Math.round((totalBytes / maxB) * 100)),
      tierName: matchedTier.name,
      price: priceStr,
      variantId: varId,
      over: totalBytes > maxB
    };
  }

  function updateCapacityDisplay() {
    var budgetEl = document.getElementById("manet-budget");
    var submitBtn = document.getElementById("manet-submit-btn");
    var totalBytes = 0;

    if (activeMode === "text") {
      var bodyEl = document.getElementById("manet-body");
      var text = bodyEl ? bodyEl.value : "";
      totalBytes = utf8Bytes(text);
    } else {
      totalBytes = selectedFile ? selectedFile.size : 0;
    }

    var info = computeTier(totalBytes);
    currentMatchedVariantId = info.variantId;

    if (budgetEl) {
      var priceDisplay = info.price ? "  •  Price: " + info.price : "";
      budgetEl.innerText = "Capacity: " + formatBytes(info.bytes) + " / " + TIER_KB + " KB (" + info.pct + "%)  •  Tier: " + info.tierName + priceDisplay;
      budgetEl.style.color = info.over ? "#ef4444" : (info.pct > 80 ? "#f59e0b" : "#d1d5db");

      var overBar = document.getElementById("manet-over-capacity-bar");
      if (info.over && selectedFile) {
        if (!overBar) {
          overBar = document.createElement("div");
          overBar.id = "manet-over-capacity-bar";
          overBar.style.cssText = "margin:10px 0; padding:12px 16px; background:rgba(245,158,11,0.12); border:1px solid rgba(245,158,11,0.3); border-radius:10px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;";
          budgetEl.parentNode.insertBefore(overBar, budgetEl.nextSibling);
        }
        overBar.innerHTML = '<div style="display:flex; align-items:center; gap:8px;"><span style="font-size:16px;">⚠️</span><span style="font-size:12.5px; color:#fde68a; font-weight:600;">File exceeds 350 KB. Auto-compress to fit Bitcoin Ordinals limit?</span></div><button type="button" id="manet-inline-compress-btn" class="manet-submit-btn" style="padding:8px 16px; font-size:12px; width:auto; margin:0; cursor:pointer;">⚡ Auto-Compress in Browser</button>';
        overBar.style.display = "flex";
        var inlineBtn = document.getElementById("manet-inline-compress-btn");
        if (inlineBtn) {
          inlineBtn.onclick = function(e) {
            e.preventDefault();
            showCompressModal(selectedFile);
            startAutoCompression();
          };
        }
      } else if (overBar) {
        overBar.style.display = "none";
      }
    }
    if (submitBtn && !submitting) {
      submitBtn.disabled = info.over;
      var pricePart = info.price ? " (" + info.price + ")" : "";
      submitBtn.innerHTML = '<span>Submit for Engraving & Pay' + pricePart + '</span> <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>';
    }
  }

  // ── Mode Switching (Pure Text vs Single Multimedia) ────────────────────────
  function setMode(mode) {
    activeMode = mode;
    var textBtn = document.getElementById("manet-mode-text-btn");
    var mediaBtn = document.getElementById("manet-mode-media-btn");
    var textPanel = document.getElementById("manet-text-panel");
    var mediaPanel = document.getElementById("manet-media-panel");

    if (mode === "text") {
      if (textBtn) { textBtn.classList.add("active"); textBtn.setAttribute("aria-selected", "true"); }
      if (mediaBtn) { mediaBtn.classList.remove("active"); mediaBtn.setAttribute("aria-selected", "false"); }
      if (textPanel) textPanel.style.display = "block";
      if (mediaPanel) mediaPanel.style.display = "none";
    } else {
      if (mediaBtn) { mediaBtn.classList.add("active"); mediaBtn.setAttribute("aria-selected", "true"); }
      if (textBtn) { textBtn.classList.remove("active"); textBtn.setAttribute("aria-selected", "false"); }
      if (mediaPanel) mediaPanel.style.display = "block";
      if (textPanel) textPanel.style.display = "none";
    }
    updateCapacityDisplay();
  }

  // ── 350KB Limit & Adaptive In-Browser Compression Engine ─────────────────
  function getMediaDuration(file, type) {
    return new Promise(function(resolve) {
      var url = URL.createObjectURL(file);
      var el = document.createElement(type);
      el.preload = 'metadata';
      el.src = url;
      el.onloadedmetadata = function() { URL.revokeObjectURL(url); resolve(isFinite(el.duration) ? el.duration : 0); };
      el.onerror = function() { URL.revokeObjectURL(url); resolve(0); };
    });
  }

  async function compressImage(file, updateProgress) {
    updateProgress(15, "Loading bitmap image...");
    var img = new Image();
    var objUrl = URL.createObjectURL(file);
    await new Promise(function(res, rej) { img.onload = res; img.onerror = rej; img.src = objUrl; });
    URL.revokeObjectURL(objUrl);

    var w = img.naturalWidth, h = img.naturalHeight, q = 0.85, blob = null;
    for (var i = 0; i < 8; i++) {
      var canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      blob = await new Promise(function(res) { canvas.toBlob(res, 'image/webp', q); });
      if (!blob) blob = await new Promise(function(res) { canvas.toBlob(res, 'image/jpeg', q); });
      updateProgress(20 + i * 10, "Pass " + (i + 1) + ": " + w + "x" + h + " (" + formatBytes(blob.size) + ")");
      if (blob.size <= HARD_LIMIT_BYTES) break;
      if (q > 0.3) { q -= 0.15; } else { w = Math.round(w * 0.8); h = Math.round(h * 0.8); }
    }
    if (blob.size > HARD_LIMIT_BYTES) throw new Error("Could not compress image under 350 KB.");
    return { blob: blob, mimeType: blob.type || 'image/webp', ext: blob.type === 'image/jpeg' ? 'jpg' : 'webp' };
  }

  async function compressAudio(file, duration, updateProgress) {
    if (duration > 240) throw new Error("Audio duration exceeds 4 minutes (" + Math.round(duration) + "s). Please trim first.");
    updateProgress(20, "Decoding audio PCM data...");
    var AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) throw new Error("AudioContext not supported in this browser.");
    var ctx = new AudioCtx();
    var buf = await file.arrayBuffer();
    var audioBuf = await ctx.decodeAudioData(buf);
    var dur = duration || audioBuf.duration || 1;
    var targetBitrate = Math.min(Math.max(Math.floor((HARD_LIMIT_BYTES * 8 * 0.9) / Math.max(dur, 1)), 12000), 128000);
    updateProgress(50, "Encoding Opus stream @ " + Math.round(targetBitrate / 1000) + " kbps...");
    var dest = ctx.createMediaStreamDestination();
    var src = ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(dest);
    var mime = 'audio/webm;codecs=opus';
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) mime = 'audio/webm';
    var rec = new MediaRecorder(dest.stream, { mimeType: mime, audioBitsPerSecond: targetBitrate });
    var chunks = [];
    rec.ondataavailable = function(e) { if (e.data.size > 0) chunks.push(e.data); };
    var done = new Promise(function(res) { rec.onstop = function() { res(new Blob(chunks, { type: mime })); }; });
    src.start(0); rec.start();
    src.onended = function() { rec.stop(); };
    var blob = await done;
    ctx.close();
    if (blob.size > HARD_LIMIT_BYTES) throw new Error("Could not compress audio under 350 KB.");
    return { blob: blob, mimeType: mime, ext: 'webm' };
  }

  async function compressVideo(file, duration, updateProgress) {
    if (duration > 15) throw new Error("Video duration exceeds 15 seconds (" + duration.toFixed(1) + "s). Please trim video first.");
    updateProgress(15, "Analyzing video track...");
    var vid = document.createElement('video');
    vid.muted = true; vid.playsInline = true;
    var vUrl = URL.createObjectURL(file);
    vid.src = vUrl;
    await new Promise(function(res, rej) { vid.onloadedmetadata = res; vid.onerror = rej; });
    var origW = vid.videoWidth || 640, origH = vid.videoHeight || 360, dur = duration || vid.duration || 5;
    var totalBits = HARD_LIMIT_BYTES * 8 * 0.88;
    var vBitrate = Math.max(16000, Math.floor(totalBits / Math.max(dur, 0.5)) - 20000);
    var fps = 15, scale = Math.min(1.0, 360 / Math.max(origW, origH));
    var finalBlob = null, chosenMime = 'video/webm', targetW = Math.round(origW * scale), targetH = Math.round(origH * scale);
    targetW = targetW % 2 === 0 ? targetW : targetW - 1;
    targetH = targetH % 2 === 0 ? targetH : targetH - 1;

    for (var pass = 0; pass < 4; pass++) {
      updateProgress(25 + pass * 20, "Pass " + (pass + 1) + ": " + targetW + "x" + targetH + " @" + fps + "FPS (" + Math.round(vBitrate/1000) + "kbps)...");
      try {
        var r = await renderVideo(vid, targetW, targetH, dur, fps, vBitrate);
        finalBlob = r.blob; chosenMime = r.mimeType;
      } catch (e) {}
      if (finalBlob && finalBlob.size <= HARD_LIMIT_BYTES) break;
      scale *= 0.75; fps = Math.max(8, Math.floor(fps * 0.8)); vBitrate = Math.floor(vBitrate * 0.75);
      targetW = Math.max(128, Math.round(origW * scale)); targetH = Math.max(96, Math.round(origH * scale));
      targetW = targetW % 2 === 0 ? targetW : targetW - 1; targetH = targetH % 2 === 0 ? targetH : targetH - 1;
    }
    URL.revokeObjectURL(vUrl);
    if (!finalBlob || finalBlob.size > HARD_LIMIT_BYTES) throw new Error("Could not compress video under 350 KB.");
    return { blob: finalBlob, mimeType: chosenMime, ext: 'webm' };
  }

  function renderVideo(video, targetW, targetH, duration, fps, bitrate) {
    return new Promise(function(resolve, reject) {
      var canvas = document.createElement('canvas');
      canvas.width = targetW; canvas.height = targetH;
      var ctx = canvas.getContext('2d');
      var stream = canvas.captureStream ? canvas.captureStream(fps) : (canvas.mozCaptureStream ? canvas.mozCaptureStream(fps) : null);
      if (!stream) return reject(new Error("Canvas captureStream not supported in this browser."));
      var mime = 'video/webm;codecs=vp9,opus';
      if (!window.MediaRecorder || !MediaRecorder.isTypeSupported(mime)) mime = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mime)) mime = 'video/webm';
      var rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrate });
      var chunks = [];
      rec.ondataavailable = function(e) { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = function() { resolve({ blob: new Blob(chunks, { type: mime }), mimeType: mime }); };
      rec.onerror = reject;
      video.currentTime = 0;
      var animId = null;
      var draw = function() {
        if (video.ended || video.currentTime >= duration) {
          if (animId) cancelAnimationFrame(animId);
          if (rec.state === 'recording') rec.stop();
          return;
        }
        ctx.drawImage(video, 0, 0, targetW, targetH);
        animId = requestAnimationFrame(draw);
      };
      rec.start(100);
      video.play().then(function() { draw(); }).catch(reject);
      setTimeout(function() { if (rec.state === 'recording') { if (animId) cancelAnimationFrame(animId); rec.stop(); } }, (duration + 3) * 1000);
    });
  }

  function showCompressModal(file) {
    pendingRawFile = file;
    var modal = document.getElementById("manet-compress-modal");
    var sizeInfo = document.getElementById("manet-compress-filesize-info");
    var choiceStep = document.getElementById("manet-compress-step-choice");
    var progressStep = document.getElementById("manet-compress-step-progress");
    var errorStep = document.getElementById("manet-compress-step-error");
    if (!modal) return;
    if (sizeInfo) sizeInfo.textContent = "Selected: " + file.name + " (" + formatBytes(file.size) + " - Cap: 350 KB)";
    if (choiceStep) choiceStep.style.display = "block";
    if (progressStep) progressStep.style.display = "none";
    if (errorStep) errorStep.style.display = "none";
    modal.style.display = "flex";
  }

  function hideCompressModal() {
    var modal = document.getElementById("manet-compress-modal");
    if (modal) modal.style.display = "none";
    pendingRawFile = null;
  }

  async function startAutoCompression() {
    var file = pendingRawFile || selectedFile;
    if (!file) return;
    pendingRawFile = file;
    var type = file.type || '';
    var choiceStep = document.getElementById("manet-compress-step-choice");
    var progressStep = document.getElementById("manet-compress-step-progress");
    var errorStep = document.getElementById("manet-compress-step-error");
    var statusText = document.getElementById("manet-compress-status-text");
    var detailText = document.getElementById("manet-compress-detail-text");
    var pBar = document.getElementById("manet-compress-progress-bar");

    if (choiceStep) choiceStep.style.display = "none";
    if (progressStep) progressStep.style.display = "block";
    if (errorStep) errorStep.style.display = "none";

    function onProgress(pct, detail) {
      if (pBar) pBar.style.width = Math.min(100, Math.max(0, Math.round(pct))) + "%";
      if (statusText) statusText.textContent = "Auto-Compressing (" + Math.round(pct) + "%)...";
      if (detailText) detailText.textContent = detail;
    }

    try {
      var res = null;
      if (type.startsWith("image/")) {
        res = await compressImage(file, onProgress);
      } else if (type.startsWith("video/")) {
        var dur = await getMediaDuration(file, 'video');
        res = await compressVideo(file, dur, onProgress);
      } else if (type.startsWith("audio/")) {
        var aDur = await getMediaDuration(file, 'audio');
        res = await compressAudio(file, aDur, onProgress);
      } else {
        throw new Error("Unsupported media type for compression.");
      }

      if (res && res.blob) {
        var compressedFile = new File([res.blob], file.name.replace(/\.[^/.]+$/, "") + "-compressed." + res.ext, { type: res.mimeType });
        hideCompressModal();
        handleMediaSelect(compressedFile);
        var savedPct = Math.max(0, Math.round((1 - compressedFile.size / file.size) * 100));
        showStatus("success", "⚡ File compressed from " + formatBytes(file.size) + " to " + formatBytes(compressedFile.size) + " (" + savedPct + "% saved) - Ready for Bitcoin inscription!");
      }
    } catch (err) {
      console.error(err);
      if (progressStep) progressStep.style.display = "none";
      if (errorStep) {
        errorStep.style.display = "block";
        var errDesc = document.getElementById("manet-compress-error-desc");
        if (errDesc) errDesc.textContent = String(err.message || err);
      }
    }
  }

  function onFileChosen(file) {
    if (!file) return;
    handleMediaSelect(file);
    if (file.size > HARD_LIMIT_BYTES) {
      showCompressModal(file);
    }
  }

  // ── Single Media Player Mount & Preview ──────────────────────────────────
  function handleMediaSelect(file) {
    if (!file) return;
    selectedFile = file;

    if (selectedFileObjectUrl) {
      try { URL.revokeObjectURL(selectedFileObjectUrl); } catch (e) {}
    }
    selectedFileObjectUrl = URL.createObjectURL(file);

    var uploader = document.getElementById("manet-uploader");
    var previewWrap = document.getElementById("manet-media-preview-wrap");
    var playerMount = document.getElementById("manet-media-player-mount");

    if (playerMount) {
      playerMount.innerHTML = "";
      var fileType = detectFileType(file);
      if (fileType === "image") {
        var img = document.createElement("img");
        img.src = selectedFileObjectUrl;
        img.alt = "Inscription preview";
        playerMount.appendChild(img);
      } else if (fileType === "video") {
        var video = document.createElement("video");
        video.src = selectedFileObjectUrl;
        video.controls = true;
        video.playsInline = true;
        playerMount.appendChild(video);
      } else if (fileType === "audio") {
        var audio = document.createElement("audio");
        audio.src = selectedFileObjectUrl;
        audio.controls = true;
        playerMount.appendChild(audio);
      } else {
        playerMount.innerHTML = '<div style="padding:16px; color:#d4af37;">📄 ' + file.name + '</div>';
      }
    }

    if (uploader) uploader.style.display = "none";
    if (previewWrap) previewWrap.style.display = "block";

    updateCapacityDisplay();
  }

  function handleMediaRemove() {
    selectedFile = null;
    if (selectedFileObjectUrl) {
      try { URL.revokeObjectURL(selectedFileObjectUrl); } catch (e) {}
      selectedFileObjectUrl = null;
    }

    var fileInput = document.getElementById("manet-file");
    if (fileInput) fileInput.value = "";

    var uploader = document.getElementById("manet-uploader");
    var previewWrap = document.getElementById("manet-media-preview-wrap");
    var playerMount = document.getElementById("manet-media-player-mount");

    if (playerMount) playerMount.innerHTML = "";
    if (previewWrap) previewWrap.style.display = "none";
    if (uploader) uploader.style.display = "block";

    updateCapacityDisplay();
  }

  // ── Scene Trait Row Creation & Validation ─────────────────────────────────
  var MAX_TRAITS = 10;

  function validateTraitKeys() {
    var container = document.getElementById("manet-traits-container");
    var errorEl = document.getElementById("manet-trait-dup-error");
    var addBtn = document.getElementById("manet-add-trait-btn");
    if (!container) return true;

    var rows = container.querySelectorAll(".manet-trait-row");
    if (addBtn) {
      if (rows.length >= MAX_TRAITS) {
        addBtn.disabled = true;
        addBtn.title = "Maximum 10 traits reached";
      } else {
        addBtn.disabled = false;
        addBtn.title = "";
      }
    }

    var keyInputs = container.querySelectorAll(".manet-trait-key");
    var keyCounts = {};
    var hasDuplicate = false;

    keyInputs.forEach(function (inp) {
      var val = inp.value.trim().toLowerCase();
      if (val) {
        keyCounts[val] = (keyCounts[val] || 0) + 1;
      }
    });

    keyInputs.forEach(function (inp) {
      var val = inp.value.trim().toLowerCase();
      if (val && keyCounts[val] > 1) {
        inp.classList.add("manet-trait-duplicate");
        hasDuplicate = true;
      } else {
        inp.classList.remove("manet-trait-duplicate");
      }
    });

    if (errorEl) {
      errorEl.style.display = hasDuplicate ? "block" : "none";
    }

    return !hasDuplicate;
  }

  function createTraitRow(initialKey, initialVal, placeholderVal) {
    var container = document.getElementById("manet-traits-container");
    if (!container) return;

    var rows = container.querySelectorAll(".manet-trait-row");
    if (rows.length >= MAX_TRAITS) return;

    var row = document.createElement("div");
    row.className = "manet-trait-row";

    var keyInput = document.createElement("input");
    keyInput.type = "text";
    keyInput.className = "manet-trait-input manet-trait-key";
    keyInput.placeholder = "Trait key";
    keyInput.value = initialKey || "";

    var valInput = document.createElement("input");
    valInput.type = "text";
    valInput.className = "manet-trait-input manet-trait-val";
    valInput.placeholder = placeholderVal || "Trait value";
    valInput.value = initialVal || "";

    var delBtn = document.createElement("button");
    delBtn.type = "button";
    delBtn.className = "manet-trait-del-btn";
    delBtn.title = "Delete Trait";
    delBtn.innerHTML = "🗑️";
    delBtn.addEventListener("click", function () {
      row.remove();
      validateTraitKeys();
    });

    keyInput.addEventListener("input", function () {
      validateTraitKeys();
    });

    row.appendChild(keyInput);
    row.appendChild(valInput);
    row.appendChild(delBtn);
    container.appendChild(row);

    validateTraitKeys();
  }

  function renderSceneTraits(scene) {
    var container = document.getElementById("manet-traits-container");
    if (!container) return;
    container.innerHTML = "";

    var schema = BUILTIN_SCHEMAS[scene] || BUILTIN_SCHEMAS.free;
    var fields = schema.metafields || [];

    if (fields.length > 0) {
      fields.forEach(function (f) {
        createTraitRow(f.label || f.key, metaValues[f.key] || "", f.placeholder || "");
      });
    } else {
      // Free Inscription scene: start with 1 empty trait row
      createTraitRow("", "", "e.g. Trait Value");
    }

    validateTraitKeys();
  }

  // ── Bitcoin Inscription ID Validation (64 hex characters + 'i' + index) ─
  function isValidInscriptionId(id) {
    if (!id || typeof id !== "string") return false;
    var trimmed = id.trim();
    if (!trimmed) return false;
    return /^[a-fA-F0-9]{64}i\d+$/.test(trimmed);
  }

  // ── Bitcoin Address Validation ──────────────────────────────────────────
  function isValidBitcoinAddress(addr) {
    if (!addr || typeof addr !== "string") return false;
    var trimmed = addr.trim();
    if (!trimmed) return false;
    var bech32Regex = /^(bc1|tb1)[0-9ac-hj-np-z]{38,90}$/i;
    var base58Regex = /^([13]|[2mn])[a-km-zA-HJ-NP-Z1-9]{25,34}$/;
    return bech32Regex.test(trimmed) || base58Regex.test(trimmed);
  }

  function expandAdvancedPanel() {
    var advContent = document.getElementById("manet-advanced-content");
    var advToggle = document.getElementById("manet-advanced-toggle");
    var advArrow = document.getElementById("manet-advanced-arrow");
    if (advContent) advContent.style.display = "block";
    if (advToggle) advToggle.setAttribute("aria-expanded", "true");
    if (advArrow) advArrow.classList.add("open");
  }

  function escapeHtml(str) {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // ── Auto-Load Compressed Asset from sessionStorage (Cross-Page Flow) ─────
  function checkAndLoadCompressedAsset() {
    try {
      var raw = sessionStorage.getItem('MANET_COMPRESSED_ASSET');
      if (!raw) return;
      var asset = JSON.parse(raw);
      if (asset && asset.dataUrl && (Date.now() - (asset.timestamp || 0) < 2 * 3600 * 1000)) {
        sessionStorage.removeItem('MANET_COMPRESSED_ASSET');

        var arr = asset.dataUrl.split(',');
        var mimeMatch = (arr[0] || '').match(/:(.*?);/);
        var mime = (mimeMatch && mimeMatch[1]) || asset.type || 'image/webp';
        var bstr = atob(arr[1] || '');
        var n = bstr.length;
        var u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        var restoredFile = new File([u8arr], asset.name || "compressed-media.webp", { type: mime });

        setMode("media");
        handleMediaSelect(restoredFile);

        showStatus("success", "✨ Automatically loaded compressed " + (asset.kind || "media") + " (" + formatBytes(restoredFile.size) + ") from Compressor Studio!");
      }
    } catch (e) {
      console.warn("Could not load compressed asset from sessionStorage:", e);
    }
  }

  // ── Status Banner Display ────────────────────────────────────────────────
  function showStatus(type, msg) {
    var el = document.getElementById("manet-status");
    if (!el) return;
    el.className = "manet-status manet-" + type;
    el.innerHTML = msg;
    el.style.display = "block";
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  // ── Direct Upload to R2 via Worker ───────────────────────────────────────
  async function uploadSingleFile(file) {
    var res = await fetch(WORKER_URL + "/upload", {
      method: "POST",
      headers: {
        "X-Manet-Key": SHARED_KEY,
        "X-Manet-Shared-Key": SHARED_KEY,
        "X-Filename": encodeURIComponent(file.name),
        "X-Kind": detectFileType(file),
        "X-Order-Id": ORDER_ID,
        "Content-Type": file.type || "application/octet-stream"
      },
      body: file
    });
    var data = await res.json();
    if (!res.ok || !data.key) {
      throw new Error(data.error || (data.detail && JSON.stringify(data.detail)) || "File upload failed");
    }
    return {
      mediaKey: data.key,
      mediaUrl: data.mediaUrl || (WORKER_URL + "/media/" + data.key)
    };
  }

  // ── Pre-Submission Double-Check Review Modal & Direct Checkout ─────────────
  function handleSubmit(e) {
    if (e) e.preventDefault();
    if (submitting) return;

    var parentSwitch = document.getElementById("manet-switch-parent");
    var titleSwitch = document.getElementById("manet-switch-title");
    var metadataSwitch = document.getElementById("manet-switch-metadata");
    var btcSwitch = document.getElementById("manet-switch-btc");
    var visSwitch = document.getElementById("manet-visibility");

    var isParentEnabled = parentSwitch ? parentSwitch.checked : false;
    var isTitleEnabled = titleSwitch ? titleSwitch.checked : false;
    var isMetadataEnabled = metadataSwitch ? metadataSwitch.checked : false;
    var isBtcEnabled = btcSwitch ? btcSwitch.checked : false;
    var visibility = (visSwitch && visSwitch.checked) ? "public" : "private";

    var titleEl = document.getElementById("manet-title");
    var parentEl = document.getElementById("manet-parent-inscription");
    var bodyEl = document.getElementById("manet-body");
    var descEl = document.getElementById("manet-description");
    var btcEl = document.getElementById("manet-btc-address");

    var parentInscriptionId = (isParentEnabled && parentEl) ? parentEl.value.trim() : "";
    var title = (isTitleEnabled && titleEl) ? titleEl.value.trim() : "";
    var description = (isTitleEnabled && descEl) ? descEl.value.trim() : "";
    var btcAddress = (isBtcEnabled && btcEl) ? btcEl.value.trim() : "";
    var bodyText = bodyEl ? bodyEl.value.trim() : "";

    // Validation per mode
    if (activeMode === "text" && !bodyText) {
      showStatus("error", "Please write the inscription text before proceeding.");
      if (bodyEl) bodyEl.focus();
      return;
    }
    if (activeMode === "media" && !selectedFile) {
      showStatus("error", "Please choose a media file (Image, GIF, Audio, or Video) to upload.");
      return;
    }

    // Strict Parent Inscription ID Validation
    if (isParentEnabled && parentInscriptionId) {
      if (!isValidInscriptionId(parentInscriptionId)) {
        showStatus("error", "Invalid Parent Inscription ID format. Expected 64 hex characters followed by 'i' and index (e.g., 6fb976ab...i0).");
        expandAdvancedPanel();
        var panelParent = document.getElementById("manet-panel-parent");
        if (panelParent) panelParent.style.display = "block";
        if (parentSwitch) parentSwitch.checked = true;
        if (parentEl) parentEl.focus();
        return;
      }
    }

    // Strict Bitcoin Address Validation
    if (isBtcEnabled && btcAddress) {
      if (!isValidBitcoinAddress(btcAddress)) {
        showStatus("error", "Invalid Bitcoin address format. Please enter a valid Taproot (bc1p...) or SegWit (bc1q...) Bitcoin address.");
        expandAdvancedPanel();
        var panelBtc = document.getElementById("manet-panel-btc");
        if (panelBtc) panelBtc.style.display = "block";
        if (btcSwitch) btcSwitch.checked = true;
        if (btcEl) btcEl.focus();
        return;
      }
    }

    // Traits Gathering & Key Deduplication Validation
    var payloadMetadata = {};
    var traitsData = {};

    if (isTitleEnabled) {
      if (!validateTraitKeys()) {
        showStatus("error", "Duplicate trait keys are not allowed. Please ensure all trait keys are unique.");
        expandAdvancedPanel();
        var panelTitle = document.getElementById("manet-panel-title");
        if (panelTitle) panelTitle.style.display = "block";
        if (titleSwitch) titleSwitch.checked = true;
        return;
      }

      var traitContainer = document.getElementById("manet-traits-container");
      if (traitContainer) {
        var tRows = traitContainer.querySelectorAll(".manet-trait-row");
        tRows.forEach(function (tr) {
          var kInp = tr.querySelector(".manet-trait-key");
          var vInp = tr.querySelector(".manet-trait-val");
          var k = kInp ? kInp.value.trim() : "";
          var v = vInp ? vInp.value.trim() : "";
          if (k) {
            payloadMetadata[k] = v;
            traitsData[k] = v;
          }
        });
      }
    }

    if (isMetadataEnabled && description) {
      payloadMetadata.description = description;
    }

    if (parentInscriptionId) {
      payloadMetadata.parent_inscription_id = parentInscriptionId;
    }

    var preparedData = {
      bodyText: bodyText,
      title: title,
      description: description,
      parentInscriptionId: parentInscriptionId,
      btcAddress: btcAddress,
      visibility: visibility,
      isTitleEnabled: isTitleEnabled,
      isMetadataEnabled: isMetadataEnabled,
      metadata: payloadMetadata,
      traits: traitsData
    };

    openDoubleCheckModal(preparedData);
  }

  function openDoubleCheckModal(preparedData) {
    var modal = document.getElementById("manet-double-check-modal");
    if (!modal) {
      executeSubmission(preparedData);
      return;
    }

    var titleEl = document.getElementById("manet-double-check-title");
    var countEl = document.getElementById("manet-check-item-count");
    var coreContentEl = document.getElementById("manet-check-core-content");
    var detailsListEl = document.getElementById("manet-check-details-list");

    if (titleEl) {
      titleEl.textContent = activeMode === "text" 
        ? "Please double check your text below before continuing:" 
        : "Please double check your media below before continuing:";
    }

    if (countEl) {
      countEl.textContent = activeMode === "text" ? "1 text" : "1 " + detectFileType(selectedFile);
    }

    if (coreContentEl) {
      if (activeMode === "text") {
        coreContentEl.innerHTML = '<div style="white-space:pre-wrap; font-size:14px; font-weight:500; color:#f8fafc; line-height:1.5;">' + escapeHtml(preparedData.bodyText) + '</div>';
      } else if (selectedFile) {
        var fileType = detectFileType(selectedFile);
        var mediaSrc = selectedFileObjectUrl || (selectedFile ? URL.createObjectURL(selectedFile) : '');
        var mediaPreviewHtml = '';

        if (fileType === 'image' && mediaSrc) {
          mediaPreviewHtml = '<div style="margin-top:8px; border-radius:8px; overflow:hidden; border:1px solid rgba(255,255,255,0.12); background:rgba(0,0,0,0.4); text-align:center; padding:6px;">' +
            '<img src="' + mediaSrc + '" alt="Inscription Media Preview" style="max-width:100%; max-height:220px; object-fit:contain; border-radius:6px; display:inline-block;" />' +
            '</div>';
        } else if (fileType === 'video' && mediaSrc) {
          mediaPreviewHtml = '<div style="margin-top:8px; border-radius:8px; overflow:hidden; border:1px solid rgba(255,255,255,0.12); background:#000; text-align:center;">' +
            '<video src="' + mediaSrc + '" controls playsinline style="max-width:100%; max-height:200px; display:block; margin:0 auto;"></video>' +
            '</div>';
        } else if (fileType === 'audio' && mediaSrc) {
          mediaPreviewHtml = '<div style="margin-top:8px; padding:10px; border-radius:8px; border:1px solid rgba(255,255,255,0.12); background:rgba(15,23,42,0.8);">' +
            '<audio src="' + mediaSrc + '" controls style="width:100%;"></audio>' +
            '</div>';
        }

        var icon = fileType === 'video' ? '🎬' : (fileType === 'audio' ? '🎵' : '🖼️');
        var typeLabel = fileType === 'image' ? 'Image Binary' : (fileType === 'video' ? 'Video Binary' : 'Audio Binary');

        coreContentEl.innerHTML = '<div style="width:100%;">' +
          '<div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">' +
            '<span style="font-size:13px; font-weight:600; color:#e2e8f0; display:flex; align-items:center; gap:6px;"><span>' + icon + '</span> <span>' + typeLabel + '</span></span>' +
            '<div style="display:flex; gap:6px;">' +
              '<span class="manet-check-badge-pill">📏 ' + formatBytes(selectedFile.size) + '</span>' +
              '<span class="manet-check-badge-pill">🏷️ ' + escapeHtml(selectedFile.type || 'Media Binary') + '</span>' +
            '</div>' +
          '</div>' +
          mediaPreviewHtml +
        '</div>';
      }
    }

    if (detailsListEl) {
      var rows = [];

      var totalBytes = activeMode === "text" ? utf8Bytes(preparedData.bodyText) : (selectedFile ? selectedFile.size : 0);
      var tierInfo = computeTier(totalBytes);
      if (tierInfo) {
        var pricePart = tierInfo.price ? ' (' + tierInfo.price + ')' : '';
        rows.push('<div class="manet-check-item-row"><span class="manet-check-key">Capacity & Tier</span><span class="manet-check-val">' + escapeHtml(tierInfo.tierName) + pricePart + '</span></div>');
      }

      if (preparedData.title) {
        rows.push('<div class="manet-check-item-row"><span class="manet-check-key">Archival Title</span><span class="manet-check-val">' + escapeHtml(preparedData.title) + '</span></div>');
      }
      if (preparedData.traits) {
        var tKeys = Object.keys(preparedData.traits);
        for (var i = 0; i < tKeys.length; i++) {
          var tk = tKeys[i];
          rows.push('<div class="manet-check-item-row"><span class="manet-check-key">Trait (' + escapeHtml(tk) + ')</span><span class="manet-check-val">' + escapeHtml(preparedData.traits[tk]) + '</span></div>');
        }
      }
      if (preparedData.description) {
        rows.push('<div class="manet-check-item-row"><span class="manet-check-key">Backstory / Notes</span><span class="manet-check-val">' + escapeHtml(preparedData.description) + '</span></div>');
      }
      if (preparedData.parentInscriptionId) {
        rows.push('<div class="manet-check-item-row"><span class="manet-check-key">Parent Inscription</span><span class="manet-check-val" style="font-family:monospace; color:#38bdf8;">' + escapeHtml(preparedData.parentInscriptionId) + '</span></div>');
      }
      if (preparedData.btcAddress) {
        rows.push('<div class="manet-check-item-row"><span class="manet-check-key">Target Taproot BTC</span><span class="manet-check-val" style="font-family:monospace; color:#f59e0b;">' + escapeHtml(preparedData.btcAddress) + '</span></div>');
      }

      detailsListEl.innerHTML = rows.join("");
    }

    modal.style.display = "flex";

    var backBtn = document.getElementById("manet-check-back-btn");
    var confirmBtn = document.getElementById("manet-check-confirm-btn");

    if (backBtn) {
      backBtn.onclick = function() {
        modal.style.display = "none";
      };
    }
    if (confirmBtn) {
      confirmBtn.onclick = function() {
        modal.style.display = "none";
        executeSubmission(preparedData);
      };
    }
  }

  async function executeSubmission(preparedData) {
    if (submitting) return;
    submitting = true;
    var submitBtn = document.getElementById("manet-submit-btn");
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerText = "Submitting Inscription & Preparing Checkout...";
    }
    showStatus("info", "Processing your inscription...");

    try {
      var contentType = "text";
      var mimeType = "text/plain;charset=utf-8";
      var mediaKey = "";
      var mediaUrl = "";

      if (activeMode === "media" && selectedFile) {
        showStatus("info", "Uploading media file (" + formatBytes(selectedFile.size) + ")...");
        var upRes = await uploadSingleFile(selectedFile);
        mediaKey = upRes.mediaKey;
        mediaUrl = upRes.mediaUrl;
        mimeType = selectedFile.type || "application/octet-stream";
        contentType = detectFileType(selectedFile);
      }

      var customerId = (cfg.customer && cfg.customer.id) || (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) || "";
      var customerEmail = (cfg.customer && cfg.customer.email) || (window.Shopify && window.Shopify.customer && window.Shopify.customer.email) || "";

      var draftPayload = {
        variantId: currentMatchedVariantId,
        title: preparedData.title || "",
        contentType: contentType,
        bodyText: activeMode === "text" ? preparedData.bodyText : "",
        mediaKey: mediaKey,
        mediaUrl: mediaUrl,
        mimeType: mimeType,
        description: preparedData.description,
        parentInscriptionId: preparedData.parentInscriptionId,
        metadata: preparedData.metadata,
        btcAddress: preparedData.btcAddress,
        visibility: preparedData.visibility,
        customerId: customerId,
        customerEmail: customerEmail
      };

      showStatus("info", "Creating Shopify Order & Inscription Record...");
      var orderRes = await fetch(WORKER_URL + "/draft-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Manet-Key": SHARED_KEY,
          "X-Manet-Shared-Key": SHARED_KEY
        },
        body: JSON.stringify(draftPayload)
      });
      var orderData = await orderRes.json();

      if (!orderRes.ok || !orderData.ok && !orderData.invoiceUrl) {
        throw new Error(orderData.error || (orderData.detail && JSON.stringify(orderData.detail)) || "Failed to create draft order");
      }

      // ⚡ ZERO-CART DIRECT CHECKOUT: Directly redirect to Shopify Checkout Invoice URL!
      showStatus("success", "✓ Inscription submitted! Redirecting to secure checkout...");

      if (orderData.invoiceUrl) {
        window.location.href = orderData.invoiceUrl;
      } else {
        window.location.href = "/checkout";
      }

    } catch (err) {
      console.error("Submission error:", err);
      showStatus("error", "Error: " + (err.message || String(err)));
      submitting = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        updateCapacityDisplay();
      }
    }
  }

  // ── Initialization & Event Wiring ─────────────────────────────────────────
  function init() {
    // Mode Buttons
    var textBtn = document.getElementById("manet-mode-text-btn");
    var mediaBtn = document.getElementById("manet-mode-media-btn");
    if (textBtn) textBtn.addEventListener("click", function () { setMode("text"); });
    if (mediaBtn) mediaBtn.addEventListener("click", function () { setMode("media"); });

    // Text Input Listener
    var bodyEl = document.getElementById("manet-body");
    if (bodyEl) {
      bodyEl.addEventListener("input", updateCapacityDisplay);
    }

    // File Input & Dropzone Wiring
    var fileInput = document.getElementById("manet-file");
    var uploader = document.getElementById("manet-uploader");
    var removeBtn = document.getElementById("manet-media-remove-btn");

    if (uploader && fileInput) {
      uploader.addEventListener("click", function (e) {
        if (e.target !== fileInput) {
          fileInput.click();
        }
      });

      uploader.addEventListener("dragover", function (e) {
        e.preventDefault();
        uploader.style.borderColor = "#d4af37";
        uploader.style.background = "rgba(212, 175, 55, 0.1)";
      });
      uploader.addEventListener("dragleave", function () {
        uploader.style.borderColor = "rgba(212, 175, 55, 0.3)";
        uploader.style.background = "rgba(255, 255, 255, 0.02)";
      });
      uploader.addEventListener("drop", function (e) {
        e.preventDefault();
        uploader.style.borderColor = "rgba(212, 175, 55, 0.3)";
        uploader.style.background = "rgba(255, 255, 255, 0.02)";
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
          onFileChosen(e.dataTransfer.files[0]);
        }
      });
    }

    if (fileInput) {
      fileInput.addEventListener("change", function () {
        if (fileInput.files && fileInput.files[0]) {
          onFileChosen(fileInput.files[0]);
        }
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener("click", handleMediaRemove);
    }

    // Modal Action Bindings
    var autoCompBtn = document.getElementById("manet-auto-compress-btn");
    if (autoCompBtn) {
      autoCompBtn.addEventListener("click", startAutoCompression);
    }
    var cancelCompBtn = document.getElementById("manet-compress-cancel-btn");
    if (cancelCompBtn) {
      cancelCompBtn.addEventListener("click", hideCompressModal);
    }

    // Advanced Section Toggle Accordion
    var advToggle = document.getElementById("manet-advanced-toggle");
    var advContent = document.getElementById("manet-advanced-content");
    var advArrow = document.getElementById("manet-advanced-arrow");

    if (advToggle && advContent) {
      advToggle.addEventListener("click", function () {
        var isOpen = advContent.style.display !== "none";
        advContent.style.display = isOpen ? "none" : "block";
        advToggle.setAttribute("aria-expanded", String(!isOpen));
        if (advArrow) {
          if (!isOpen) advArrow.classList.add("open");
          else advArrow.classList.remove("open");
        }
      });
    }

    // Switch Toggles & Expandable Panels Wiring
    function wireToggle(switchId, panelId, rowId) {
      var sw = document.getElementById(switchId);
      var panel = document.getElementById(panelId);
      var row = document.getElementById(rowId);
      if (!sw || !panel) return;

      function updatePanel() {
        if (sw.checked) {
          panel.style.display = "block";
        } else {
          panel.style.display = "none";
        }
      }

      sw.addEventListener("change", updatePanel);
      if (row) {
        row.addEventListener("click", function (e) {
          if (e.target !== sw && !sw.contains(e.target) && !e.target.closest(".manet-switch")) {
            sw.checked = !sw.checked;
            updatePanel();
          }
        });
      }
    }

    wireToggle("manet-switch-parent", "manet-panel-parent", "manet-toggle-row-parent");
    wireToggle("manet-switch-title", "manet-panel-title", "manet-toggle-row-title");
    wireToggle("manet-switch-metadata", "manet-panel-metadata", "manet-toggle-row-metadata");
    wireToggle("manet-switch-btc", "manet-panel-btc", "manet-toggle-row-btc");

    // Parent Inscription ID Live Validation
    var parentInscInput = document.getElementById("manet-parent-inscription");
    var parentFeedback = document.getElementById("manet-parent-feedback");
    if (parentInscInput) {
      parentInscInput.addEventListener("input", function () {
        var val = parentInscInput.value.trim();
        if (!parentFeedback) return;
        if (!val) {
          parentFeedback.style.display = "none";
          parentInscInput.style.borderColor = "rgba(212, 175, 55, 0.3)";
          return;
        }
        if (isValidInscriptionId(val)) {
          parentFeedback.style.display = "block";
          parentFeedback.style.color = "#34d399";
          parentFeedback.innerHTML = "✓ Valid Bitcoin Inscription ID (" + val.slice(0, 10) + "..." + val.slice(-4) + ")";
          parentInscInput.style.borderColor = "#34d399";
        } else {
          parentFeedback.style.display = "block";
          parentFeedback.style.color = "#f87171";
          if (val.length === 64 && /^[a-fA-F0-9]{64}$/.test(val)) {
            parentFeedback.innerHTML = "⚠️ 64-hex transaction ID detected, but missing inscription index suffix (e.g. add <strong>i0</strong> at the end).";
          } else {
            parentFeedback.innerHTML = "⚠️ Invalid format. Must be 64-hex characters followed by 'i' and index (e.g. ...i0). Current length: " + val.length + " chars.";
          }
          parentInscInput.style.borderColor = "#f87171";
        }
      });
    }

    // Target BTC Address Live Validation
    var btcAddressInput = document.getElementById("manet-btc-address");
    var btcFeedback = document.getElementById("manet-btc-feedback");
    if (btcAddressInput) {
      btcAddressInput.addEventListener("input", function () {
        var val = btcAddressInput.value.trim();
        if (!btcFeedback) return;
        if (!val) {
          btcFeedback.style.display = "none";
          btcAddressInput.style.borderColor = "rgba(212, 175, 55, 0.3)";
          return;
        }
        if (isValidBitcoinAddress(val)) {
          btcFeedback.style.display = "block";
          btcFeedback.style.color = "#34d399";
          var typeTag = val.startsWith("bc1p") ? "Taproot (bc1p)" : (val.startsWith("bc1q") ? "SegWit (bc1q)" : "Legacy/Script");
          btcFeedback.innerHTML = "✓ Valid Bitcoin " + typeTag + " Address";
          btcAddressInput.style.borderColor = "#34d399";
        } else {
          btcFeedback.style.display = "block";
          btcFeedback.style.color = "#f87171";
          btcFeedback.innerHTML = "⚠️ Invalid Bitcoin address format (expected bc1p... or bc1q...)";
          btcAddressInput.style.borderColor = "#f87171";
        }
      });
    }

    // Auto-prefill Default BTC Address from Customer Settings if available
    if (btcAddressInput && !btcAddressInput.value && cfg.customer && cfg.customer.defaultBtcAddress) {
      btcAddressInput.value = cfg.customer.defaultBtcAddress;
      var btcSw = document.getElementById("manet-switch-btc");
      var btcPanel = document.getElementById("manet-panel-btc");
      if (btcSw && btcPanel) {
        btcSw.checked = true;
        btcPanel.style.display = "block";
      }
    }

    // Title Clear Button & Add Trait Button
    var addTraitBtn = document.getElementById("manet-add-trait-btn");
    if (addTraitBtn) {
      addTraitBtn.addEventListener("click", function () {
        createTraitRow("", "", "Trait value");
      });
    }

    var titleInput = document.getElementById("manet-title");
    var titleClearBtn = document.getElementById("manet-title-clear-btn");
    if (titleInput && titleClearBtn) {
      titleInput.addEventListener("input", function () {
        titleClearBtn.style.display = titleInput.value.length > 0 ? "block" : "none";
      });
      titleClearBtn.addEventListener("click", function () {
        titleInput.value = "";
        titleClearBtn.style.display = "none";
        titleInput.focus();
      });
    }

    // Form Submit
    var form = document.getElementById("manet-memorial-form");
    if (form) {
      form.addEventListener("submit", handleSubmit);
    }

    // Initial Scene Traits Render
    renderSceneTraits(currentSceneCode);
    updateCapacityDisplay();

    // Check for incoming compressed media from compressor standalone page
    checkAndLoadCompressedAsset();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

})();
